/**
 * nav.ts — ЄДИНЕ джерело істини для навігації React-клієнта.
 *
 * Дзеркалить бекенд:
 *   - ролі            → app/services/db_users.py (ROLE_*)
 *   - landing         → app/auth/rbac.py::resolve_landing()
 *   - структуру меню  → app/auth/rbac.py::menu_items_for()
 *
 * Правило (CLAUDE.md §4): нові пункти меню додаємо ТУТ, не дублюємо у
 * компонентах. AdminShell / WorkerShell / BottomNav читають звідси.
 */

// ── Канонічні ролі (рядкові значення = ті, що приходять у JWT claims.roles) ──
export const ROLES = {
  ADMIN: "admin",
  MASTER: "master",
  MENEDZHER: "menedzher",
  ZAKRIYNYK: "zakriynyk",
  SHVEYA: "shveya",
  OTK: "otk",
  VTO: "vto",
  TSYKLICHKA: "tsyklichka",
  PAKUVALNYK: "pakuvalnyk",
  PRINTER: "printer",
  VIDGRUZKA: "vidgruzka",
  PIDRYADNYK_MGR: "pidryadnyky_mgr",
  KONSTRUKTOR: "konstruktor",
  BUHGALTER: "buhgalter",
  GLAV_TEKHNOLOG: "glav_tekhnolog",
  SKLAD_TKANYNY: "sklad_tkanyny",
  SKLAD_FURNITURY: "sklad_furnitury",
} as const;

/** Людські назви ролей — тут, бо nav.ts і так єдине джерело про ролі. */
export const ROLE_LABEL: Record<string, string> = {
  admin: "Адміністратор",
  master: "Технолог",
  menedzher: "Менеджер",
  zakriynyk: "Закрійник",
  shveya: "Швея",
  otk: "ОТК",
  vto: "ВТО",
  tsyklichka: "Циклічка",
  pakuvalnyk: "Пакувальник",
  printer: "Друкар",
  vidgruzka: "Відвантаження",
  pidryadnyky_mgr: "Підрядники",
  konstruktor: "Конструктор",
  buhgalter: "Бухгалтер",
  glav_tekhnolog: "Головний технолог",
  sklad_tkanyny: "Склад тканини",
  sklad_furnitury: "Склад фурнітури",
};

export const roleUa = (r: string): string => ROLE_LABEL[r] ?? r;

/** Хто веде довідник типів операцій (Паша 16.10.2026) — дзеркало
 *  `operations.WRITE_ROLES`. Решта лише обирає з нього. */
export const CATALOG_ROLES = [ROLES.GLAV_TEKHNOLOG, ROLES.ADMIN];

// ── Landing за роллю (дзеркало resolve_landing, але на наявні React-маршрути) ──
// Порядок = пріоритет. Поки відповідний екран не збудовано — ведемо на
// найближчий наявний маршрут (позначено коментарем "fallback").
const LANDING: Array<[string, string]> = [
  // Головна, а не Пульт: Пульт — це рідкісні дії (сервер, логи, знищення), і
  // відкривати його першим екраном щодня означає щоразу починати не з роботи.
  [ROLES.ADMIN, "/admin"],
  [ROLES.MASTER, "/admin"],
  [ROLES.GLAV_TEKHNOLOG, "/constructor"],
  [ROLES.MENEDZHER, "/orders"],
  [ROLES.OTK, "/qc"],
  [ROLES.ZAKRIYNYK, "/cutting"],
  [ROLES.PAKUVALNYK, "/packing"],
  [ROLES.SHVEYA, "/sewing"],
  [ROLES.VTO, "/vto"],
  [ROLES.TSYKLICHKA, "/tsyklichka"],
  [ROLES.PRINTER, "/printing"], // кабінет друкаря вже збудовано
  [ROLES.VIDGRUZKA, "/shipping"],
  [ROLES.PIDRYADNYK_MGR, "/orders"], // fallback: /contractors ще не збудовано
  // Без цього рядка конструктор падав у fallback /orders, куди бек його не
  // пускає (ORDER_READ_ROLES не містить konstruktor) — тобто в 403 одразу
  // після входу. Та сама пастка, що колись була з pidryadnyky_mgr.
  [ROLES.KONSTRUKTOR, "/constructor"],
  [ROLES.BUHGALTER, "/admin/salary"],
  [ROLES.SKLAD_TKANYNY, "/stock/wh/fabric"],
  [ROLES.SKLAD_FURNITURY, "/stock/wh/accessories"],
];

/** Куди вести того, у кого ролі немає взагалі.
 *
 *  Куплено 04.09.2026: fallback `/orders` пускав таку людину на екран, у
 *  якого немає `requireRoles`, тож guard мовчав, екран малювався, і аж тоді
 *  бек віддавав 403. Виходило порожнє меню + порожній список + тост, який
 *  зникає, — і жодної підказки, що робити. Чотири людини з бейджами на
 *  руках щоразу впирались саме в це. */
export const NO_SCREEN_PATH = "/no-screen";

export function landingForRoles(roles: string[] | undefined | null): string {
  const set = new Set(roles ?? []);
  for (const [role, path] of LANDING) {
    if (set.has(role)) return path;
  }
  // Порожньо або роль, якої система не знає (спеціальність без екрана —
  // оздоблення, водій): чесний напис, а не `/orders`, де людину зустрічала
  // англійська 403 «Insufficient role» (скарга 26.09.2026).
  return NO_SCREEN_PATH;
}

// ── Структура меню (дзеркало menu_items_for) ──
export interface NavItem {
  to: string;
  label: string;
  icon: string; // emoji-іконка (тимчасово, до icon-set)
  /** Короткий підпис для НИЖНЬОЇ навігації телефона.
   *
   *  Там слот ≈69 px на пункт (пʼять пунктів на 375 px), тобто ~12 знаків.
   *  Доти довгі підписи просто обрізались: «ОТК · Приймання» ставало «ОТК ·…»,
   *  «Аналітика та план» — «Аналіти…», і людина не розуміла, куди веде
   *  вкладка. Скорочення пишемо руками, а не ріжемо кодом: «Аналітика» — це
   *  осмислене слово, а «Аналіти» — ні. */
  short?: string;
  roles: string[]; // хто бачить пункт (порожній = всі автентифіковані)
  /** Ключ розділу в таблиці прав. Мусить збігатися з `SECTIONS_FULL`
   *  у `app/services/db_access_const.py`. Без нього право береться за
   *  першим сегментом шляху — а це не завжди те саме (журнал завдань
   *  живе на /tasks, але ходить у /api/v1/qc). */
  sect?: string;
  /** Лише для входу через Google — за бейджем пункт не показуємо. */
  googleOnly?: boolean;
  children?: NavItem[]; // під-пункти (випадаюче меню в сайдбарі)
  /** Окремий вхід у вкладку іншого пункту: показуємо лише тим, кому той
   *  пункт НЕ видно (інакше три «Склади» підряд). Через таблицю прав, а не
   *  лише роль: рядок прав на розділ показує пункт за будь-якої ролі. */
  onlyWithout?: string;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

const M = ROLES;

/** Повна канонічна навігація. */
export const NAV: NavGroup[] = [
  {
    title: "Огляд",
    items: [
      // Робочий стіл (Danyil 02.11.2026: «головну бачитиме і менеджер, і
      // власник, і адміни»). Вміст фільтрується правами кожного розділу.
      { to: "/admin", label: "Головна", icon: "🏠", sect: "admin-home",
        roles: [M.ADMIN, M.MASTER, M.MENEDZHER, M.BUHGALTER] },
      { to: "/owner/dashboard", label: "Власник", icon: "💎", sect: "dashboard", roles: [M.ADMIN] },
      // Контроль внесення (ТЗ2 04.11.2026): хто що вніс по днях — для тих, хто видає роботу.
      { to: "/control", label: "Контроль внесення", short: "Контроль", icon: "🔎", sect: "control",
        roles: [M.ADMIN, M.MASTER, M.MENEDZHER] },
      {
        to: "/technolog/dashboard",
        label: "Аналітика та план",
        short: "Аналітика",
        icon: "📊",
        sect: "technolog",
        roles: [M.MASTER, M.ADMIN, M.MENEDZHER],
      },
      // «Норми часу» більше не окремий пункт: вони стали пʼятою вкладкою
      // кабінету технолога (`TechnologTabs`). Ролі маршруту ті самі, тож
      // ніхто доступу не втратив — просто на один рядок у меню менше.
      {
        // R&D: дошка розробки моделі + калькуляція. Конструктор веде розробку,
        // але собівартість бачать лише керівні (бек: MONEY_ROLES).
        to: "/constructor",
        label: "Конструктор",
        icon: "✏",
        sect: "constructor",
        roles: [M.KONSTRUKTOR, M.MASTER, M.ADMIN, M.MENEDZHER, M.GLAV_TEKHNOLOG],
      },
      {
        // Довідник типів операцій (Паша 16.10.2026): веде головний технолог,
        // технолог-лаборант і конструктор лише переглядають і обирають. У меню —
        // вкладкою «Конструктора» (`CONSTRUCTOR_TABS`), окремим пунктом лише тим,
        // хто конструктора не бачить (Danyil 02.11.2026: «забагато сторінок»).
        to: "/constructor/operations",
        label: "Типи операцій",
        icon: "🧵",
        sect: "constructor",
        onlyWithout: "/constructor",
        roles: [M.GLAV_TEKHNOLOG, M.MASTER, M.ADMIN, M.KONSTRUKTOR],
      },
    ],
  },
  {
    title: "Робота",
    items: [
      {
        // Гроші: каси, прихід/розхід, борги. Залишки — лише директор (admin)
        // і бухгалтер (власник 08.10.2026); менеджер лише вносить (12.10).
        to: "/cash",
        label: "Гроші",
        icon: "₴",
        sect: "cash",
        // Гроші — лише через Google (Danyil 14.10.2026), не за бейджем.
        googleOnly: true,
        roles: [M.ADMIN, M.BUHGALTER, M.MENEDZHER],
      },
      {
        // Склад: закупівлі, залишки, борги постачальникам. Цех не бачить —
        // тут ціни (роль-фільтр збігається з STOCK_ROLES на беку).
        to: "/stock",
        label: "Склад",
        icon: "📥",
        sect: "stock",
        roles: [M.MENEDZHER, M.MASTER, M.ADMIN, M.BUHGALTER],
      },
      {
        // Вузли складу (Паша 16.10.2026): накладні з двома погодженнями,
        // залишки в метрах без цін, розкрої без метрів. Окремим пунктом — лише
        // тим, хто не бачить «Склад»; решта ходить вкладками (`STOCK_TABS`),
        // бо три «Склади» підряд у меню (Danyil 02.11.2026) — забагато.
        to: "/stock/wh/fabric",
        label: "Склад тканини",
        icon: "🧵",
        sect: "stock",
        onlyWithout: "/stock",
        roles: [M.ADMIN, M.MENEDZHER, M.BUHGALTER, M.SKLAD_TKANYNY, M.ZAKRIYNYK],
      },
      {
        to: "/stock/wh/accessories",
        label: "Склад фурнітури",
        icon: "📎",
        sect: "stock",
        onlyWithout: "/stock",
        roles: [M.ADMIN, M.MENEDZHER, M.BUHGALTER, M.SKLAD_FURNITURY],
      },
      {
        to: "/orders",
        label: "Замовлення",
        icon: "📦",
        sect: "orders",
        // pidryadnyky_mgr: бек (ORDER_READ_ROLES) його пускає, і його лендінг —
        // теж /orders; без цього пункту він логінився у ПОРОЖНЄ меню й не мав
        // жодного посилання нікуди.
        // Конструктор (власник 05.10.2026): створює й править заявки, друкує карти.
        roles: [M.ADMIN, M.MASTER, M.MENEDZHER, M.BUHGALTER, M.OTK, M.PIDRYADNYK_MGR, M.KONSTRUKTOR],
      },
      {
        to: "/tasks",
        label: "Журнал завдань",
        short: "Задачі",
        icon: "✅",
        sect: "tasks",
        roles: [M.OTK, M.MASTER, M.MENEDZHER, M.ADMIN],
      },
      {
        to: "/problems",
        label: "Проблеми",
        icon: "🆘",
        sect: "alerts",
        roles: [M.OTK, M.MASTER, M.MENEDZHER, M.ADMIN],
      },
    ],
  },
  {
    title: "Цех",
    items: [
      // «Технолог · кабінет» прибрано (2026-07-17): вело на той самий
      // /technolog/dashboard, що й «Аналітика та план» (Огляд) — дубль.
      {
        to: "/cutting",
        // «Мій розкрій», а не просто «Розкрій»: людина з ролями закрійника Й
        // керівного бачить обидва пункти, і однакові підписи не давали
        // зрозуміти, куди йдеш — робити чи дивитись.
        label: "Мій розкрій",
        icon: "✂",
        sect: "cutting",
        // Тільки закрійник: це РОБОЧИЙ стіл, а не оглядовий екран. Керівні
        // дивляться крій через «Звіт» і «Журнал» нижче — так само, як пошив
        // і ВТО вони дивляться не «входом» у кабінет швеї.
        roles: [M.ZAKRIYNYK],
      },
      // Нагляд за кроєм — ОДИН екран, уже без вкладок усередині.
      // Спершу (03.09) два пункти меню звели в один із двома вкладками
      // «Звіт»/«Журнал». Власник подивився й попросив звести до кінця
      // (04.09): «забрати взагалі вкладку, і коли натискаєш на замовлення —
      // там одразу можна змінювати». Тепер картка показує все, а редагування
      // циклів розгортається в ній же; `/cutting/journal` — редірект сюди.
      //
      // Окремими пунктами (а не дітьми «Розкрою») лишається важливо:
      // `navGroupsForRoles` фільтрує спочатку батька, тож усередині
      // закрійницького пункту нагляд зник би в керівних разом із ним.
      {
        // Кабінет швеї — суто особистий (запити визначають швею з її токена).
        // Керівні наглядають через Дашборд/Гант/Швей, а не «входом» у кабінет.
        to: "/sewing",
        label: "Пошив",
        icon: "🧵",
        sect: "sewing",
        roles: [M.SHVEYA],
      },
      {
        // Кабінет ВТО — робочий екран (як у швеї), лише для ВТО-працівника.
        // Керівні наглядають через замовлення/картку, а не «входом» у кабінет.
        to: "/vto",
        label: "ВТО",
        icon: "♨",
        sect: "vto",
        roles: [M.VTO],
      },
      {
        // Циклічка — окрема дільниця (власник 03.10.2026): свій пул операцій,
        // ВТО їх не бачить.
        to: "/tsyklichka",
        label: "Циклічка",
        icon: "🌀",
        sect: "tsyklichka",
        roles: [M.TSYKLICHKA],
      },
      {
        to: "/qc",
        label: "ОТК · Приймання",
        short: "ОТК",
        icon: "✓",
        sect: "qc",
        // Тільки ОТК: приймання — це ДІЯ, а не перегляд.
        roles: [M.OTK],
      },
      {
        // Той самий екран, але керівні заходять на ЗВІТИ, а не на чергу
        // приймання: `?tab=` обирає вкладку, і черга їм не показується.
        to: "/qc?tab=reports",
        label: "ОТК · Звіти та брак",
        short: "Брак",
        icon: "📈",
        sect: "qc",
        roles: [M.ADMIN, M.MASTER, M.MENEDZHER],
      },
      {
        to: "/packing",
        label: "Упаковка",
        icon: "📦",
        sect: "packing",
        // ВТО у нас теж пакує.
        roles: [M.PAKUVALNYK, M.VTO, M.MASTER, M.ADMIN, M.MENEDZHER],
      },
      {
        // Друкареві — окремий пункт: він робить лише бирки, і смужки вкладок
        // не бачить (дві з трьох дали б йому 403).
        to: "/printing",
        label: "Друк бирок",
        icon: "🖨",
        sect: "printing",
        roles: [M.PRINTER],
      },
      {
        // ОДИН пункт на весь процес: друк → відвантаження → накладна.
        // Доти це були ТРИ пункти меню під одну послідовність дій.
        // Перемикач усередині — `ShippingTabs`.
        to: "/shipping",
        label: "Відвантаження",
        short: "Відправка",
        icon: "🚚",
        sect: "shipping",
        roles: [M.VIDGRUZKA, M.MENEDZHER, M.MASTER, M.ADMIN],
      },
      {
        // Лише перегляд: що накроєно й що далі, і завершене за кроєм не
        // зникає (власник 01.10.2026). Цеху — поіменно в «Правах».
        // Останнім у групі: у ВТО нижнє меню бере перші 5, і свої робочі
        // «ВТО» та «Упаковка» мусять іти раніше.
        to: "/progress",
        label: "Хід замовлень",
        // «Хід», а не «Крій»: у Циклічки й ВТО «Крій» читається як чужий екран.
        short: "Хід",
        icon: "📋",
        sect: "progress",
        roles: [M.ZAKRIYNYK, M.TSYKLICHKA, M.MASTER, M.ADMIN, M.MENEDZHER],
      },
    ],
  },
  {
    title: "Персонал / Адмін",
    items: [
      {
        // «Працівники» — єдиний вхід замість Швеї/Користувачі/Права/
        // Відвідуваність (консолідація 2026-08-16). Старі маршрути лишились
        // робочими для прямих посилань, але з меню прибрані.
        to: "/admin/people",
        label: "Працівники",
        icon: "👥",
        sect: "seamstresses",
        roles: [M.ADMIN, M.MASTER, M.MENEDZHER],
      },
      {
        // Зарплати — бухгалтер, адмін і технолог (2026-08-17).
        to: "/admin/salary",
        label: "Зарплати",
        icon: "💰",
        sect: "salary",
        roles: [M.ADMIN, M.BUHGALTER, M.MASTER],
      },
      {
        to: "/admin/contractors",
        label: "Контрагенти",
        icon: "🏢",
        sect: "contractors",
        roles: [M.ADMIN, M.MASTER, M.MENEDZHER, M.BUHGALTER],
      },
      // «Кошик і відкат» — одне місце, де повертають зроблене (власник
      // 26.09.2026): відкат останніх дій + колишній Кошик із Пульта.
      // Адмінське, не щоденне — тому тут, а не в «Огляді» (27.09.2026).
      {
        to: "/trash",
        label: "Кошик і відкат",
        short: "Відкат",
        icon: "↶",
        roles: [M.ADMIN, M.MASTER, M.MENEDZHER, M.BUHGALTER],
      },
      // Пульт — службове, рідкісне: в самому низу, а не першим у меню
      // (власник 28.09.2026). Поіменно забирається в «Правах» («admin.panel»).
      { to: "/admin/panel", label: "Пульт", icon: "🛠", sect: "admin.panel", roles: [M.ADMIN] },
    ],
  },
];

/** Розділ, до якого належить пункт меню: /admin/stock → "stock" не годиться,
 *  тому беремо ключ явно зі шляху першого сегмента, як робить бек
 *  (`_section_of` у jwt_rbac). Персональні права (Блок 7) прив'язані саме
 *  до розділу, і меню має ховати/показувати те саме, що дозволяє бек. */
function sectionOf(to: string): string {
  return to.replace(/^\//, "").split("/")[0] ?? "";
}

type NavOpts = { access?: Record<string, string>; kind?: string };

/** Чи пункт видно людині — роль + персональні права (без `onlyWithout`). */
function visibleFor(roles: string[] | undefined | null, opts: NavOpts): (it: NavItem) => boolean {
  const set = new Set(roles ?? []);
  const access = opts.access ?? {};
  const visible = (it: NavItem): boolean => {
    if (it.googleOnly && opts.kind === "seamstress") return false;
    // Рівень доступу: «none» ховає пункт, «view» і «full» — показують
    // (перегляд теж має бути видно, просто без кнопок збереження).
    //
    // `hasOwn`, а не просто `access[key]`: розділ «Конструктор» має
    // `sect: "constructor"`, а `({})["constructor"]` повертає успадкований від
    // `Object.prototype` конструктор — НЕ `undefined`. Через це перевірка
    // вирішувала «рівень заданий і це не none» і показувала пункт КОЖНІЙ ролі:
    // швеї, закрійнику, пакувальнику, друкарю. Те саме чекало б на будь-який
    // `sect` з іменем `toString`, `valueOf`, `hasOwnProperty`.
    const key = it.sect ?? sectionOf(it.to);
    if (Object.prototype.hasOwnProperty.call(access, key)) {
      return access[key] !== "none";
    }
    return it.roles.length === 0 || it.roles.some((r) => set.has(r));
  };
  return visible;
}

/** Чи людині відкритий шлях меню — для вкладок розділу (`SectionTabs`) і
 *  головної. Те саме джерело, що й меню: ролі й права не дублюються. */
export function canOpenNav(roles: string[] | undefined | null, opts: NavOpts, to: string): boolean {
  const visible = visibleFor(roles, opts);
  return NAV.some((g) => g.items.some((it) => (it.to === to && visible(it))
    || (it.children ?? []).some((ch) => ch.to === to && visible(it) && visible(ch))));
}

/** Групи меню, відфільтровані за ролями користувача + персональними правами.
 *  Порожні групи прибираємо. */
export function navGroupsForRoles(
  roles: string[] | undefined | null,
  opts: NavOpts = {},
): NavGroup[] {
  const visible = visibleFor(roles, opts);
  const mapItem = (it: NavItem): NavItem =>
    it.children ? { ...it, children: it.children.filter(visible) } : it;
  const shown = new Set(NAV.flatMap((g) => g.items.filter(visible).map((it) => it.to)));
  const keep = (it: NavItem) => visible(it) && !(it.onlyWithout && shown.has(it.onlyWithout));
  return NAV.map((g) => ({
    ...g,
    items: g.items.filter(keep).map(mapItem),
  })).filter((g) => g.items.length > 0);
}

/**
 * Який пункт меню підсвітити для поточного шляху: найдовший збіг префікса
 * (тільки один активний — «Головна» /admin не світиться разом з /admin/*,
 * а /orders/5 тримає «Замовлення» активним). Якщо точного збігу нема
 * (напр. /technolog/accept) — фолбек по першому сегменту шляху.
 */
export function activeNavPath(groups: NavGroup[], pathname: string): string | null {
  const all: string[] = [];
  for (const g of groups) {
    for (const it of g.items) {
      all.push(it.to);
      for (const c of it.children ?? []) all.push(c.to);
    }
  }
  const norm = pathname.replace(/\/+$/, "") || "/";
  let best: string | null = null;
  for (const to of all) {
    if (norm === to || norm.startsWith(to + "/")) {
      if (!best || to.length > best.length) best = to;
    }
  }
  if (best) return best;
  const seg = "/" + (norm.split("/")[1] ?? "");
  let fallback: string | null = null;
  for (const to of all) {
    if (to === seg || to.startsWith(seg + "/")) {
      if (!fallback || to.length < fallback.length) fallback = to;
    }
  }
  return fallback;
}

/**
 * Хлібні крихти для активного шляху: [Розділ, Пункт(, Підпункт)].
 * Джерело — те саме дерево NAV (нового джерела не заводимо).
 */
export function breadcrumbTrail(
  groups: NavGroup[],
  activeTo: string | null,
): string[] {
  if (!activeTo) return [];
  for (const g of groups) {
    for (const it of g.items) {
      if (it.to === activeTo) return [g.title, it.label];
      for (const c of it.children ?? []) {
        if (c.to === activeTo) return [g.title, it.label, c.label];
      }
    }
  }
  return [];
}

/**
 * Чи дає роль доступ до розділу «за замовчуванням» — тобто без жодних
 * налаштувань у таблиці прав. Джерело те саме, що й меню: `NAV`.
 * `undefined` — розділу немає в меню (напр. «Нарахування»), отже сказати
 * нічого не можемо: там усе вирішує роль на беку.
 */
export function defaultAllows(sect: string, role: string): boolean | undefined {
  let known = false;
  for (const g of NAV) {
    for (const it of g.items) {
      for (const node of [it, ...(it.children ?? [])]) {
        if ((node.sect ?? sectionOf(node.to)) !== sect) continue;
        known = true;
        if (node.roles.length === 0 || node.roles.includes(role)) return true;
      }
    }
  }
  return known ? false : undefined;
}

/** Плаский перелік пунктів (для BottomNav швеї — топ-N). */
export function navItemsFlat(
  roles: string[] | undefined | null,
  opts: { access?: Record<string, string> } = {},
): NavItem[] {
  return navGroupsForRoles(roles, opts).flatMap((g) => g.items);
}
